import React, { useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Checkbox,
  FormControlLabel,
  IconButton,
  Collapse,
  Chip,
} from '@mui/material';
import {
  Check as CheckIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Delete as DeleteIcon,
  DragIndicator as DragIndicatorIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { PageHeader } from '@/components/ui';

interface Step1Data {
  credentialsAcquisition: string;
  name: string;
  clientId: string;
  secretKey: string;
}

interface CostData {
  id: string;
  conditions: Array<{
    type: string;
    conditional: string;
  }>;
  successfulPayment: {
    percentage: number;
    fixedFee: number;
    currency: string;
  };
  unsuccessfulPayment: {
    percentage: number;
    fixedFee: number;
    currency: string;
  };
  isExpanded?: boolean;
}

interface Step2Data {
  costs: CostData[];
}

interface Step3Data {
  selectedAccounts: string[];
  searchTerm: string;
}

const steps = ['Set up credentials', 'Set up costs', 'Set up accounts'];

export const ConnectionSetupPage: React.FC = () => {
  const navigate = useNavigate();
  const { gatewayName } = useParams<{ gatewayName?: string }>();
  const [activeStep, setActiveStep] = useState(0);
  
  // Step 1 State
  const [step1Data, setStep1Data] = useState<Step1Data>({
    credentialsAcquisition: '',
    name: '',
    clientId: '',
    secretKey: '',
  });

  // Step 2 State
  const [step2Data, setStep2Data] = useState<Step2Data>({
    costs: [
      {
        id: 'default',
        conditions: [],
        successfulPayment: { percentage: 0, fixedFee: 0, currency: 'USD' },
        unsuccessfulPayment: { percentage: 0, fixedFee: 0, currency: 'USD' },
        isExpanded: true,
      },
    ],
  });

  // Step 3 State
  const [step3Data, setStep3Data] = useState<Step3Data>({
    selectedAccounts: ['current'],
    searchTerm: '',
  });

  const [availableAccounts] = useState([
    { id: 'current', name: 'Current account only' },
    { id: 'account1', name: 'Account 1' },
    { id: 'child', name: 'Child Account' },
  ]);

  const handleNext = () => {
    if (activeStep === 0) {
      // Validate step 1
      if (!step1Data.name || !step1Data.clientId || !step1Data.secretKey) {
        return;
      }
    }
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleCancel = () => {
    navigate('/merchant/connections');
  };

  const handleSave = () => {
    // TODO: Implement save logic
    navigate('/merchant/connections');
  };

  const isStep1Valid = step1Data.name && step1Data.clientId && step1Data.secretKey;
  const isStep3Valid = step3Data.selectedAccounts.length > 0;

  // Step 1 Handlers
  const handleStep1Change = (field: keyof Step1Data, value: string) => {
    setStep1Data((prev) => ({ ...prev, [field]: value }));
  };

  // Step 2 Handlers
  const handleAddCost = () => {
    const newCost: CostData = {
      id: `cost-${Date.now()}`,
      conditions: [{ type: '', conditional: '' }],
      successfulPayment: { percentage: 0, fixedFee: 0, currency: 'USD' },
      unsuccessfulPayment: { percentage: 0, fixedFee: 0, currency: 'USD' },
      isExpanded: true,
    };
    setStep2Data((prev) => ({
      ...prev,
      costs: [...prev.costs, newCost],
    }));
  };

  const handleDeleteCost = (costId: string) => {
    setStep2Data((prev) => ({
      ...prev,
      costs: prev.costs.filter((cost) => cost.id !== costId),
    }));
  };

  const handleToggleCostExpanded = (costId: string) => {
    setStep2Data((prev) => ({
      ...prev,
      costs: prev.costs.map((cost) =>
        cost.id === costId ? { ...cost, isExpanded: !cost.isExpanded } : cost
      ),
    }));
  };

  const handleCostChange = (
    costId: string,
    field: 'successfulPayment' | 'unsuccessfulPayment',
    subField: 'percentage' | 'fixedFee' | 'currency',
    value: string | number
  ) => {
    setStep2Data((prev) => ({
      ...prev,
      costs: prev.costs.map((cost) =>
        cost.id === costId
          ? {
              ...cost,
              [field]: {
                ...cost[field],
                [subField]: value,
              },
            }
          : cost
      ),
    }));
  };

  const handleAddCondition = (costId: string) => {
    setStep2Data((prev) => ({
      ...prev,
      costs: prev.costs.map((cost) =>
        cost.id === costId
          ? {
              ...cost,
              conditions: [...cost.conditions, { type: '', conditional: '' }],
            }
          : cost
      ),
    }));
  };

  // Step 3 Handlers
  const handleAccountToggle = (accountId: string) => {
    setStep3Data((prev) => ({
      ...prev,
      selectedAccounts: prev.selectedAccounts.includes(accountId)
        ? prev.selectedAccounts.filter((id) => id !== accountId)
        : [...prev.selectedAccounts, accountId],
    }));
  };

  const handleSelectAll = () => {
    setStep3Data((prev) => ({
      ...prev,
      selectedAccounts: availableAccounts.map((acc) => acc.id),
    }));
  };

  const handleInverse = () => {
    setStep3Data((prev) => ({
      ...prev,
      selectedAccounts: availableAccounts
        .map((acc) => acc.id)
        .filter((id) => !prev.selectedAccounts.includes(id)),
    }));
  };

  const filteredAccounts = availableAccounts.filter((acc) =>
    acc.name.toLowerCase().includes(step3Data.searchTerm.toLowerCase())
  );

  return (
    <Box>
      <PageHeader
        title={gatewayName ? `Add ${gatewayName}` : 'Add Connection'}
        showDivider={false}
      />

      {/* Progress Stepper */}
      <Box sx={{ mb: 4 }}>
        <Stepper activeStep={activeStep} alternativeLabel>
          {steps.map((label, index) => (
            <Step key={label}>
              <StepLabel
                StepIconComponent={({ active, completed }) => (
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: completed
                        ? 'primary.main'
                        : active
                        ? 'primary.main'
                        : 'grey.300',
                      color: completed || active ? 'white' : 'grey.600',
                    }}
                  >
                    {completed ? (
                      <CheckIcon sx={{ fontSize: 20 }} />
                    ) : (
                      <Typography variant="body2" fontWeight="bold">
                        {index + 1}
                      </Typography>
                    )}
                  </Box>
                )}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: activeStep >= index ? 'primary.main' : 'text.secondary',
                    fontWeight: activeStep === index ? 600 : 400,
                  }}
                >
                  {label}
                </Typography>
              </StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>

      {/* Step Content */}
      <Paper sx={{ p: 4, mb: 3 }}>
        {/* Step 1: Set up credentials */}
        {activeStep === 0 && (
          <Box>
            <Typography variant="h5" fontWeight="bold" gutterBottom>
              1. Set up credentials
            </Typography>

            <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
              <FormControl fullWidth>
                <InputLabel>Credentials acquisition</InputLabel>
                <Select
                  value={step1Data.credentialsAcquisition}
                  onChange={(e) =>
                    handleStep1Change('credentialsAcquisition', e.target.value)
                  }
                  label="Credentials acquisition"
                >
                  <MenuItem value="manual">Manual Entry</MenuItem>
                  <MenuItem value="oauth">OAuth</MenuItem>
                </Select>
              </FormControl>

              <TextField
                fullWidth
                label="Name"
                placeholder="Enter a name"
                value={step1Data.name}
                onChange={(e) => handleStep1Change('name', e.target.value)}
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&.Mui-focused fieldset': {
                      borderColor: 'primary.main',
                    },
                  },
                }}
              />

              <TextField
                fullWidth
                label="Client ID"
                placeholder="Enter Client ID"
                value={step1Data.clientId}
                onChange={(e) => handleStep1Change('clientId', e.target.value)}
                required
              />

              <TextField
                fullWidth
                label="Secret Key"
                placeholder="Enter Secret Key"
                type="password"
                value={step1Data.secretKey}
                onChange={(e) => handleStep1Change('secretKey', e.target.value)}
                required
              />
            </Box>
          </Box>
        )}

        {/* Step 2: Set up costs */}
        {activeStep === 1 && (
          <Box>
            <Typography variant="h5" fontWeight="bold" gutterBottom>
              2. Set up costs (Optional)
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 3 }}>
              This will help you optimize your costs by using Smart Routing. Costs are
              evaluated in their creation order, but you can reorder them by dragging and
              dropping as needed.
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {step2Data.costs.map((cost, index) => (
                <Paper
                  key={cost.id}
                  variant="outlined"
                  sx={{
                    p: 3,
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mb: 2,
                    }}
                  >
                    <Typography variant="h6" fontWeight="bold">
                      {index === 0 ? 'Cost for all payments' : `Cost ${index + 1}`}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {index > 0 && (
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteCost(cost.id)}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      )}
                      <IconButton
                        size="small"
                        onClick={() => handleToggleCostExpanded(cost.id)}
                      >
                        {cost.isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                    </Box>
                  </Box>

                  <Collapse in={cost.isExpanded}>
                    {/* Conditions */}
                    {index > 0 && (
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                          Conditions
                        </Typography>
                        {cost.conditions.map((condition, condIndex) => (
                          <Box
                            key={condIndex}
                            sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}
                          >
                            <Chip
                              label={`Condition ${condIndex + 1}`}
                              size="small"
                              sx={{ minWidth: 100 }}
                            />
                            <FormControl size="small" sx={{ minWidth: 200 }}>
                              <InputLabel>Select condition type</InputLabel>
                              <Select
                                value={condition.type}
                                label="Select condition type"
                                onChange={(e) => {
                                  const newConditions = [...cost.conditions];
                                  newConditions[condIndex].type = e.target.value;
                                  setStep2Data((prev) => ({
                                    ...prev,
                                    costs: prev.costs.map((c) =>
                                      c.id === cost.id
                                        ? { ...c, conditions: newConditions }
                                        : c
                                    ),
                                  }));
                                }}
                              >
                                <MenuItem value="amount">Amount</MenuItem>
                                <MenuItem value="currency">Currency</MenuItem>
                                <MenuItem value="country">Country</MenuItem>
                              </Select>
                            </FormControl>
                            <FormControl size="small" sx={{ minWidth: 200 }}>
                              <InputLabel>Select conditional</InputLabel>
                              <Select
                                value={condition.conditional}
                                label="Select conditional"
                                onChange={(e) => {
                                  const newConditions = [...cost.conditions];
                                  newConditions[condIndex].conditional = e.target.value;
                                  setStep2Data((prev) => ({
                                    ...prev,
                                    costs: prev.costs.map((c) =>
                                      c.id === cost.id
                                        ? { ...c, conditions: newConditions }
                                        : c
                                    ),
                                  }));
                                }}
                              >
                                <MenuItem value="equals">Equals</MenuItem>
                                <MenuItem value="greater">Greater than</MenuItem>
                                <MenuItem value="less">Less than</MenuItem>
                              </Select>
                            </FormControl>
                          </Box>
                        ))}
                        <Button
                          size="small"
                          onClick={() => handleAddCondition(cost.id)}
                          sx={{ color: 'primary.main', textTransform: 'none' }}
                        >
                          + Add condition
                        </Button>
                      </Box>
                    )}

                    {/* Successful payment cost */}
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                        Successful payment cost
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                        <TextField
                          label="Percentage"
                          type="number"
                          value={cost.successfulPayment.percentage}
                          onChange={(e) =>
                            handleCostChange(
                              cost.id,
                              'successfulPayment',
                              'percentage',
                              parseFloat(e.target.value) || 0
                            )
                          }
                          size="small"
                          sx={{ width: 150 }}
                          InputProps={{
                            endAdornment: <InputAdornment position="end">%</InputAdornment>,
                          }}
                        />
                        <Typography>+</Typography>
                        <TextField
                          label="Fixed fee"
                          type="number"
                          value={cost.successfulPayment.fixedFee}
                          onChange={(e) =>
                            handleCostChange(
                              cost.id,
                              'successfulPayment',
                              'fixedFee',
                              parseFloat(e.target.value) || 0
                            )
                          }
                          size="small"
                          sx={{ width: 150 }}
                        />
                        <FormControl size="small" sx={{ minWidth: 100 }}>
                          <Select
                            value={cost.successfulPayment.currency}
                            onChange={(e) =>
                              handleCostChange(
                                cost.id,
                                'successfulPayment',
                                'currency',
                                e.target.value
                              )
                            }
                          >
                            <MenuItem value="USD">USD</MenuItem>
                            <MenuItem value="INR">INR</MenuItem>
                            <MenuItem value="EUR">EUR</MenuItem>
                          </Select>
                        </FormControl>
                      </Box>
                    </Box>

                    {/* Unsuccessful payment cost */}
                    <Box>
                      <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                        Unsuccessful payment cost
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                        <TextField
                          label="Percentage"
                          type="number"
                          value={cost.unsuccessfulPayment.percentage}
                          onChange={(e) =>
                            handleCostChange(
                              cost.id,
                              'unsuccessfulPayment',
                              'percentage',
                              parseFloat(e.target.value) || 0
                            )
                          }
                          size="small"
                          sx={{ width: 150 }}
                          InputProps={{
                            endAdornment: <InputAdornment position="end">%</InputAdornment>,
                          }}
                        />
                        <Typography>+</Typography>
                        <TextField
                          label="Fixed fee"
                          type="number"
                          value={cost.unsuccessfulPayment.fixedFee}
                          onChange={(e) =>
                            handleCostChange(
                              cost.id,
                              'unsuccessfulPayment',
                              'fixedFee',
                              parseFloat(e.target.value) || 0
                            )
                          }
                          size="small"
                          sx={{ width: 150 }}
                        />
                        <FormControl size="small" sx={{ minWidth: 100 }}>
                          <Select
                            value={cost.unsuccessfulPayment.currency}
                            onChange={(e) =>
                              handleCostChange(
                                cost.id,
                                'unsuccessfulPayment',
                                'currency',
                                e.target.value
                              )
                            }
                          >
                            <MenuItem value="USD">USD</MenuItem>
                            <MenuItem value="INR">INR</MenuItem>
                            <MenuItem value="EUR">EUR</MenuItem>
                          </Select>
                        </FormControl>
                      </Box>
                    </Box>
                  </Collapse>
                </Paper>
              ))}

              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Are there different costs associated with this connection?
                </Typography>
                <Button
                  variant="text"
                  onClick={handleAddCost}
                  sx={{ color: 'primary.main', textTransform: 'none' }}
                >
                  Add new cost
                </Button>
              </Box>
            </Box>
          </Box>
        )}

        {/* Step 3: Set up accounts */}
        {activeStep === 2 && (
          <Box>
            <Typography variant="h5" fontWeight="bold" gutterBottom>
              3. Set up accounts
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 3 }}>
              The changes will be applied to Account 1 account. Do you want to apply them
              to other accounts too? Please note that the changes will only affect the
              current navigation mode.
            </Typography>

            <Box>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Apply this connection to:
              </Typography>

              <TextField
                fullWidth
                placeholder="Search"
                value={step3Data.searchTerm}
                onChange={(e) =>
                  setStep3Data((prev) => ({ ...prev, searchTerm: e.target.value }))
                }
                size="small"
                sx={{ mb: 2, mt: 2 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Typography variant="body2">🔍</Typography>
                    </InputAdornment>
                  ),
                }}
              />

              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <Button
                  variant="text"
                  onClick={handleSelectAll}
                  sx={{ color: 'primary.main', textTransform: 'none' }}
                >
                  Select all
                </Button>
                <Button
                  variant="text"
                  onClick={handleInverse}
                  sx={{ color: 'primary.main', textTransform: 'none' }}
                >
                  Inverse
                </Button>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {filteredAccounts.map((account) => (
                  <FormControlLabel
                    key={account.id}
                    control={
                      <Checkbox
                        checked={step3Data.selectedAccounts.includes(account.id)}
                        onChange={() => handleAccountToggle(account.id)}
                        sx={{
                          color: 'primary.main',
                          '&.Mui-checked': {
                            color: 'primary.main',
                          },
                        }}
                      />
                    }
                    label={account.name}
                  />
                ))}
              </Box>
            </Box>
          </Box>
        )}
      </Paper>

      {/* Navigation Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
        <Box sx={{ display: 'flex', gap: 2 }}>
          {activeStep > 0 && (
            <Button
              variant="outlined"
              onClick={handleBack}
              sx={{
                textTransform: 'none',
                borderColor: 'primary.main',
                color: 'primary.main',
              }}
            >
              Back
            </Button>
          )}
          <Button
            variant="outlined"
            onClick={handleCancel}
            sx={{
              textTransform: 'none',
              borderColor: 'primary.main',
              color: 'primary.main',
            }}
          >
            Cancel
          </Button>
        </Box>
        <Box>
          {activeStep < steps.length - 1 ? (
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={activeStep === 0 && !isStep1Valid}
              sx={{
                textTransform: 'none',
                bgcolor: activeStep === 0 && !isStep1Valid ? 'grey.300' : 'primary.main',
                color: 'white',
                '&:hover': {
                  bgcolor: activeStep === 0 && !isStep1Valid ? 'grey.300' : 'primary.dark',
                },
              }}
            >
              Next
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={!isStep3Valid}
              sx={{
                textTransform: 'none',
                bgcolor: !isStep3Valid ? 'grey.300' : 'primary.main',
                color: 'white',
                '&:hover': {
                  bgcolor: !isStep3Valid ? 'grey.300' : 'primary.dark',
                },
              }}
            >
              Save
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default ConnectionSetupPage;

